import { Badge, BadgeColor, Column } from '@odin-labs/components';
import {
  DeviceHealth,
  SiteControllerFailureCode,
  type SiteControllerFieldsFragment,
} from 'apollo/generated/client-operations';
import React from 'react';
import { getFriendlyFormattedDateTime } from 'utils/dates';

export type SiteControllerRow = SiteControllerFieldsFragment;
export type SiteControllerColumn = Column<SiteControllerRow>;

export const healthLabels: Record<DeviceHealth, string> = {
  [DeviceHealth.Online]: 'Online',
  [DeviceHealth.Offline]: 'Offline',
  [DeviceHealth.Unknown]: 'Unknown',
};

const healthColors: Record<DeviceHealth, BadgeColor> = {
  [DeviceHealth.Online]: 'green',
  [DeviceHealth.Offline]: 'red',
  [DeviceHealth.Unknown]: 'gray',
};

export const failureLabels: Record<SiteControllerFailureCode, string> = {
  [SiteControllerFailureCode.ProviderUnavailable]: 'Avigilon unreachable',
  [SiteControllerFailureCode.IncompleteSnapshot]: 'Incomplete read',
  [SiteControllerFailureCode.ControllerMissing]: 'Gone from Avigilon',
};

/** The unit's health as the latest sync read it. */
export function HealthBadge({
  controller,
}: {
  controller: Pick<SiteControllerRow, 'health'>;
}): React.ReactElement {
  return (
    <Badge
      text={healthLabels[controller.health]}
      color={healthColors[controller.health]}
    />
  );
}

/** When the controller last synced, and the last failure if it is newer. */
export const lastSyncText = (
  controller: Pick<
    SiteControllerRow,
    'lastSuccessfulSyncAt' | 'lastFailureCode'
  >,
): string => {
  const synced = controller.lastSuccessfulSyncAt
    ? getFriendlyFormattedDateTime(controller.lastSuccessfulSyncAt)
    : 'Never';
  return controller.lastFailureCode
    ? `${synced} · ${failureLabels[controller.lastFailureCode]}`
    : synced;
};

enum SiteControllerColumnId {
  Name = 'name',
  SerialNumber = 'serialNumber',
  Health = 'health',
  Readers = 'readers',
  LastSync = 'lastSync',
}

export const getColumns = (): SiteControllerColumn[] => [
  {
    id: SiteControllerColumnId.Name,
    Header: 'Controller',
    accessor: 'name',
    Cell: ({
      row,
    }: {
      row: { original: SiteControllerRow };
    }): React.ReactElement => (
      <span className="odin-flex odin-flex-col odin-items-start odin-gap-1">
        <span>{row.original.name}</span>
        {row.original.modelKey && (
          <span className="odin-text-xs odin-text-gray-500">
            Model {row.original.modelKey}
          </span>
        )}
      </span>
    ),
  },
  {
    id: SiteControllerColumnId.SerialNumber,
    Header: 'Serial number',
    accessor: (row: SiteControllerRow): string =>
      row.serialNumber ?? 'Not recorded',
  },
  {
    id: SiteControllerColumnId.Health,
    Header: 'Health',
    accessor: (row: SiteControllerRow): string => healthLabels[row.health],
    Cell: ({
      row,
    }: {
      row: { original: SiteControllerRow };
    }): React.ReactElement => <HealthBadge controller={row.original} />,
  },
  {
    id: SiteControllerColumnId.Readers,
    Header: 'Readers',
    accessor: (row: SiteControllerRow): string => {
      const missing = row.readers.filter(({ missing: gone }) => gone).length;
      return missing
        ? `${row.readers.length} (${missing} missing)`
        : String(row.readers.length);
    },
  },
  {
    id: SiteControllerColumnId.LastSync,
    Header: 'Last sync',
    accessor: (row: SiteControllerRow): string => lastSyncText(row),
  },
];
